import path from 'path';
import { WebStorageUrlStorage } from 'tus-js-client/lib/browser/urlStorage';
import { useEffect, useMemo } from 'react';
import { useUppyEvent } from '@uppy/react';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import getFingerprint from '@uppy/tus/lib/getFingerprint';
import authProvider from '@/auth/authProvider';
import XHRAuthUpload from './uppyXhrAuthUploadPlugin';

const isDevelopment = () => process.env.NODE_ENV === 'development';

const uppyDebug = () => (isDevelopment() ? Uppy.debugLogger : false);

export const uppyTusBaseOptions = {
  chunkSize: 5 * 1024 * 1024,
  retryDelays: [0, 1000, 3000, 5000],
};

export const uppyBaseOptions = {
  autoProceed: true,
  allowMultipleUploads: true,
  debug: uppyDebug(),
  restrictions: {
    minFileSize: 1,
  },
};

export const assignDataToDictionaryUpload = {
  allowMultipleUploads: false,
  debug: uppyDebug(),
  restrictions: {
    maxNumberOfFiles: 1,
    allowedFileTypes: ['.csv'],
  },
  info: {
    hidden: false,
  },
  autoProceed: true,
};

const addAuthorizationHeader = async (request) => {
  const token = await authProvider().getIdToken();
  request.setHeader('Authorization', `Bearer ${token}`);
};

const refreshAuthToken = async () => {
  await authProvider().reauthenticateUser();
};

/**
 * Keeping uppy instances cached here so when unmounting/rerendering we don't lose them or recreate them.
 * This enables us to start a long upload and navigate to whatever other part of the app while the upload continues
 * and coming back to the dataset page will correctly show the progress.
 */
const cachedUppy = {};

/**
 * Uppy hook also handling auth token on authorization header.
 *
 * @param {Object} uppyOptions
 * @param {*} tusOptions Options to be passed to Tus plugin.
 * Do not pass `headers.Authorization` as it's already handled by this hook
 * and the library concatenates the values on .setHeader for some reason :confused:.
 *
 * @returns {Uppy}
 */
export const useUppyTus = (uppyOptions, tusOptions) => {
  const uppy = useMemo(() => {
    const uppyId = tusOptions.id;

    if (cachedUppy[uppyId]) {
      return cachedUppy[uppyId];
    }

    const uppyInstance = new Uppy({
      ...uppyOptions,
    }).use(Tus, {
      ...tusOptions,
      headers: () => ({
        ...tusOptions?.headers,
      }),
      onBeforeRequest: async (request) => addAuthorizationHeader(request),
      onAfterResponse: async (_request, response) => {
        const status = response.getStatus();

        if (status === 401) {
          await refreshAuthToken();
        }
      },
    });

    cachedUppy[uppyId] = uppyInstance;

    return uppyInstance;
  }, [tusOptions.id]);

  return uppy;
};

export const selectFilePath = (filePath, fairPath) => {
  if (filePath && fairPath) {
    return path.join(fairPath, filePath);
  }
  return filePath || fairPath || '';
};

/**
 * Uppy sets the relativePath null or folder/subfolder/file.ext.
 */
export const normalizeUppyPath = (uppyPath) => {
  if (!uppyPath) {
    return undefined;
  }

  const normalized = path.normalize(uppyPath);

  if (path.extname(normalized)) {
    return path.dirname(normalized);
  }

  return normalized;
};

export const useUppyAddFolderSupport = (uppy, currentPath = undefined) => {
  useEffect(() => {
    uppy.setOptions({
      onBeforeUpload: (files) => {
        const filesObject = {};

        Object.values(files).forEach((file) => {
          const newRelativePath = selectFilePath(normalizeUppyPath(file.meta.relativePath), currentPath);

          filesObject[file.id] = {
            ...file,
            meta: {
              ...file.meta,
              relativePath: newRelativePath,
            },
          };
        });

        return filesObject;
      },
      onBeforeFileAdded: (file) => {
        if (file.extension === 'DS_Store') {
          return false;
        }

        return true;
      },
    });
  }, [currentPath, uppy]);
};

export const useUppy = (uppyOptions, options) => {
  const uppy = useMemo(() => {
    const uppyId = options.id;

    if (cachedUppy[uppyId]) {
      return cachedUppy[uppyId];
    }

    const uppyInstance = new Uppy({
      ...uppyOptions,
    });

    cachedUppy[uppyId] = uppyInstance;

    return uppyInstance;
  }, [options.id]);
  return uppy;
};

export const useUppyXhr = (uppyOptions, xhrOptions) => {
  const uppy = useMemo(() => {
    const uppyId = xhrOptions.id;

    if (cachedUppy[uppyId]) {
      return cachedUppy[uppyId];
    }

    const uppyInstance = new Uppy({
      ...uppyOptions,
    }).use(XHRAuthUpload, {
      ...xhrOptions,
    });

    cachedUppy[uppyId] = uppyInstance;

    return uppyInstance;
  }, [xhrOptions.id]);

  return uppy;
};

export const useClearUppyFingerprintOnSuccess = (uppy, tusOptions) => {
  useUppyEvent(uppy, 'upload-success', async (file) => {
    const storage = new WebStorageUrlStorage();
    const fingerprint = await getFingerprint(file)(file, tusOptions);
    const uploads = await storage.findUploadsByFingerprint(fingerprint);

    uploads.forEach((upload) => {
      storage.removeUpload(upload.urlStorageKey);
    });
  });
};

export const getUppyInstance = (id) => cachedUppy[id];
